import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import useSeo from '@/hooks/useSeo';

export default function CaseEngenhariaConfiabilidadeCilindrosAmarradeiraPage() {
  useSeo({
    title: 'Engenharia de Confiabilidade em Cilindros Hidráulicos | Oficina Especializada | Grupo Pilar',
    description: 'Case de engenharia de confiabilidade aplicada à recuperação de cilindros hidráulicos de amarradeira industrial, aumentando significativamente a vida útil dos equipamentos e reduzindo falhas prematuras.',
    keywords: 'cilindro hidráulico, engenharia de confiabilidade, amarradeira industrial, manutenção de cilindro hidráulico, recuperação de cilindro hidráulico, hidráulica industrial, papel e celulose, oficina especializada, grupo pilar',
    canonical: 'https://www.pilarmanutencao.com.br/cases/engenharia-confiabilidade-cilindros-amarradeira',
    ogUrl: 'https://www.pilarmanutencao.com.br/cases/engenharia-confiabilidade-cilindros-amarradeira',
    ogImage: 'https://readdy.ai/api/search-image?query=Extremely%20realistic%20professional%20industrial%20photography%20modern%20clean%20organized%20heavy%20industrial%20workshop%20with%20large%20machinery%20hydraulic%20equipment%20steel%20components%20metallic%20surfaces%20professional%20technicians%20in%20safety%20gear%20working%20not%20facing%20camera%20cinematic%20dramatic%20lighting%20gray%20silver%20steel%20tones%20with%20subtle%20warm%20yellow%20accent%20details%20wide%20angle%20widescreen%20composition%20premium%20high%20resolution%20sharp%20depth%20industrial%20maintenance%20engineering%20reliability%20heavy%20mechanics%20pulp%20paper%20mining%20oil%20gas%20sectors%20negative%20space%20for%20title%20overlay%20clean%20backgrounds%20not%20stock%20photo%20not%20illustration%20not%203D%20render&width=1600&height=700&seq=gp-cases-unified-hero-v2&orientation=landscape',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const heroImage = 'https://readdy.ai/api/search-image?query=Extremely%20realistic%20professional%20industrial%20photography%20modern%20clean%20organized%20heavy%20industrial%20workshop%20with%20large%20machinery%20hydraulic%20equipment%20steel%20components%20metallic%20surfaces%20professional%20technicians%20in%20safety%20gear%20working%20not%20facing%20camera%20cinematic%20dramatic%20lighting%20gray%20silver%20steel%20tones%20with%20subtle%20warm%20yellow%20accent%20details%20wide%20angle%20widescreen%20composition%20premium%20high%20resolution%20sharp%20depth%20industrial%20maintenance%20engineering%20reliability%20heavy%20mechanics%20pulp%20paper%20mining%20oil%20gas%20sectors%20negative%20space%20for%20title%20overlay%20clean%20backgrounds%20not%20stock%20photo%20not%20illustration%20not%203D%20render&width=1600&height=700&seq=gp-cases-unified-hero-v2&orientation=landscape';

  return (
    <div className="min-h-screen bg-gp-graphite">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={heroImage}
              alt="Cilindro hidráulico de amarradeira industrial recuperado pela Pilar com engenharia de confiabilidade aplicada — análise de modos de falha, melhorias técnicas, substituição de componentes críticos, montagem especializada e validação operacional"
              title="Engenharia de Confiabilidade Aplicada à Recuperação de Cilindros Hidráulicos de Amarradeira — Case Pilar"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gp-graphite/75"></div>
          </div>
          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 pt-28 pb-16">
            <div className="max-w-4xl">
              <div className="flex items-center gap-3 mb-6 flex-wrap">
                <span className="inline-block px-3 py-1 rounded-sm text-xs font-semibold uppercase tracking-wider bg-gp-yellow text-gp-graphite">
                  Oficina Especializada
                </span>
                <span className="text-gray-400 text-xs uppercase tracking-wider">Papel e Celulose</span>
                <span className="text-gray-500 text-xs">— Aracruz, ES</span>
              </div>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase leading-tight mb-6">
                <span className="text-gp-yellow">Case:</span>{' '}
                Engenharia de Confiabilidade Aplicada à Recuperação de Cilindros Hidráulicos de Amarradeira
              </h1>
              <p className="text-gray-300 text-lg max-w-2xl leading-relaxed">
                Estudo técnico completo de engenharia de confiabilidade aplicado a cilindros hidráulicos de
                amarradeira industrial com histórico de falhas prematuras, vazamentos recorrentes e baixa
                vida útil. Após análise de modos de falha, identificação de causas raiz e implementação de
                melhorias técnicas, a vida útil dos cilindros foi ampliada de poucas semanas para períodos
                superiores a oito meses de operação contínua, chegando a aproximadamente um ano em alguns casos.
              </p>
            </div>
          </div>
        </section>

        {/* Visão Geral */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Visão Geral</h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-6 md:p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-information-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Dados do Projeto</h3>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-settings-3-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Equipamento</span>
                        <p className="text-gray-300 text-sm">Cilindros Hidráulicos de Amarradeira Industrial para Sistema de Acionamento</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-building-2-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Segmento</span>
                        <p className="text-gray-300 text-sm">Papel e Celulose</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-tools-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Aplicação</span>
                        <p className="text-gray-300 text-sm">Sistema hidráulico de amarradeira industrial em processo produtivo</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-brain-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Abordagem</span>
                        <p className="text-gray-300 text-sm">Engenharia de Confiabilidade com análise de modos de falha, causa raiz e melhorias técnicas</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-bar-chart-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Ganho de Vida Útil</span>
                        <p className="text-gray-300 text-sm">Ampliação de poucas semanas para mais de 8 meses de operação contínua — até aproximadamente 1 ano em alguns casos</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-lightbulb-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Diferencial do Projeto</span>
                        <p className="text-gray-300 text-sm">Primeiro case de engenharia de confiabilidade com análise de causa raiz e ganho comprovado de vida útil superior a 8x</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <img
                    src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/af92cfec-49dd-4b37-950c-1583c3ea67d3_compressed_INSPECAO-FINAL-MONTAGEM.webp"
                    alt="Vista explodida dos componentes recuperados durante o processo de engenharia de confiabilidade"
                    title="Vista explodida dos componentes do cilindro hidráulico de amarradeira recuperados"
                    className="w-full h-auto object-contain"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Vista explodida dos componentes recuperados durante o processo de engenharia de confiabilidade.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Desafio */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Desafio</h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 md:p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-alert-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="text-gray-300 text-base leading-relaxed mb-4">
                        A Pilar desenvolveu uma solução de engenharia de confiabilidade para cilindros hidráulicos
                        utilizados em amarradeiras industriais do setor de papel e celulose. Os equipamentos
                        apresentavam histórico recorrente de falhas prematuras, vazamentos e indisponibilidade
                        operacional que comprometiam a produtividade e elevavam os custos de manutenção. Após
                        estudo técnico detalhado da aplicação, a Pilar implementou melhorias que elevaram
                        significativamente a vida útil dos cilindros em operação.
                      </p>
                      <p className="text-gray-400 text-sm leading-relaxed mb-4">
                        As principais complexidades envolviam:
                      </p>
                      <ul className="space-y-2 mb-4">
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Falhas recorrentes em operação com histórico de indisponibilidade dos cilindros</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Vazamentos prematuros que comprometiam a estanqueidade do sistema hidráulico</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Quebras de componentes com necessidade de substituições frequentes</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Baixa vida útil operacional — falhas ocorriam em poucas semanas de uso</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Elevado custo de manutenção corretiva com intervenções frequentes</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Alta criticidade para o processo produtivo com impacto direto na produção</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <img
                    src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/90137d85-62c0-4d32-91a2-7713850f861d_compressed_CAMISA-CILINDRO-AMARRADEIRA-FABRICADA.webp"
                    alt="Camisa fabricada pela Pilar para substituição de componente crítico identificado durante a análise técnica"
                    title="Camisa fabricada para cilindro hidráulico de amarradeira — análise de confiabilidade Pilar"
                    className="w-full h-auto object-contain"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Camisa fabricada pela Pilar para substituição de componente crítico identificado durante a análise técnica.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Análise de Confiabilidade */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Análise de Confiabilidade</h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start mb-8">
                <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 md:p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-search-eye-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Metodologia Aplicada</h3>
                    </div>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed mb-4">
                    Diferentemente de uma recuperação convencional, a Pilar aplicou metodologia de engenharia de
                    confiabilidade para entender por que os cilindros falhavam prematuramente. A abordagem foi
                    além do reparo — buscou-se eliminar as causas fundamentais das falhas recorrentes.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Análise completa da aplicação: condições operacionais, ciclos de trabalho, pressões e temperaturas de serviço</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Avaliação dos modos de falha: identificação de todos os mecanismos de degradação atuantes nos cilindros</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Inspeção detalhada dos componentes com peritagem técnica de hastes, tubos, vedações e guias</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Análise das condições operacionais reais versus especificações de projeto do equipamento</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Identificação das causas dos problemas recorrentes — causa raiz de cada modo de falha</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Estudo de melhorias aplicáveis ao equipamento com seleção de materiais e componentes otimizados</span>
                    </li>
                  </ul>
                </div>

                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <img
                    src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/0187013b-69b2-4f08-96c9-4dce6e5f0436_compressed_BUCHA-GUIA-HASTE-CILINDRO-AMARRADEIRA.webp"
                    alt="Bucha guia de haste desenvolvida pela Pilar para aumento da confiabilidade operacional do cilindro"
                    title="Bucha guia de haste desenvolvida para aumentar a confiabilidade do cilindro de amarradeira"
                    className="w-full h-72 md:h-80 object-contain p-4 bg-gp-graphite"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Bucha guia de haste desenvolvida pela Pilar para aumento da confiabilidade operacional do cilindro.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Escopo Executado */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Escopo Executado pela Pilar</h2>
              </div>

              <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter mb-8">
                <img
                  src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/2c6029d0-4515-4399-9274-547723f17eeb_compressed_EMBOLO-COM-VEDACAO-CILINDRO-AMARRADEIRA.webp"
                  alt="Êmbolo com vedacao do cilindro hidráulico de amarradeira — componente central da recuperação"
                  title="Êmbolo com vedacao do cilindro de amarradeira — oficina Pilar"
                  className="w-full h-auto object-contain p-4 bg-gp-graphite"
                  loading="lazy"
                />
                <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                  <p className="text-gray-400 text-xs text-center">
                    Êmbolo com vedacao do cilindro hidráulico de amarradeira — componente central da recuperação.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">01</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Desmontagem completa dos cilindros hidráulicos na oficina especializada Pilar</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">02</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Inspeção dimensional detalhada de todos os componentes internos dos cilindros</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">03</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Inspeção dos componentes internos com verificação de desgastes, folgas e condições de serviço</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">04</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Avaliação das hastes com verificação de cromagem, riscos, empenamentos e desgastes superficiais</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">05</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Avaliação dos tubos com verificação de ovalização, desgastes internos e integridade estrutural</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">06</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Substituição de componentes críticos identificados durante a análise de confiabilidade</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">07</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Aplicação de melhorias técnicas desenvolvidas a partir da análise de confiabilidade</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">08</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Montagem especializada com controle dimensional rigoroso e ajustes de precisão</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">09</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Testes hidráulicos de estanqueidade com validação de pressão de serviço</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">10</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Validação operacional com acompanhamento do desempenho em condições reais de serviço</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Resultado Alcançado — Antes e Depois */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Resultado Alcançado</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                {/* Antes */}
                <div className="relative rounded-lg overflow-hidden border border-red-500/20 bg-gp-graphite p-6 md:p-8">
                  <div className="absolute top-0 left-0 w-1 h-full bg-red-500"></div>
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-red-500/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-close-circle-line text-red-400 text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-bold text-red-300 uppercase mb-2">Antes das Melhorias</h3>
                    </div>
                  </div>
                  <div className="bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-timer-line text-red-400 w-4 h-4 flex items-center justify-center"></i>
                      <span className="text-red-400 font-semibold text-sm uppercase tracking-wider">Vida Útil Média</span>
                    </div>
                    <p className="text-gray-200 text-2xl font-bold font-display">Poucas semanas de operação</p>
                    <p className="text-gray-400 text-xs mt-1">Falhas prematuras recorrentes, vazamentos e quebras de componentes</p>
                  </div>
                </div>

                {/* Depois */}
                <div className="relative rounded-lg overflow-hidden border border-gp-yellow/20 bg-gp-graphite p-6 md:p-8">
                  <div className="absolute top-0 left-0 w-1 h-full bg-gp-yellow"></div>
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-bold text-gp-yellow uppercase mb-2">Após Melhorias Pilar</h3>
                    </div>
                  </div>
                  <div className="bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-arrow-up-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      <span className="text-gp-yellow font-semibold text-sm uppercase tracking-wider">Vida Útil Ampliada</span>
                    </div>
                    <p className="text-gray-200 text-2xl font-bold font-display">Mais de 8 meses de operação contínua</p>
                    <p className="text-gray-400 text-xs mt-1">Chegando a aproximadamente 1 ano em alguns casos — desgaste natural, sem falhas prematuras</p>
                  </div>
                </div>
              </div>

              <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 md:p-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Evolução da Condição Operacional</h3>
                  </div>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Além do aumento expressivo da vida útil, os equipamentos passaram a apresentar desgaste natural
                  de operação, eliminando completamente as falhas prematuras e ocorrências recorrentes que
                  caracterizavam o histórico anterior. A mudança de paradigma — de manutenção corretiva frequente
                  para desgaste natural previsível — representa o sucesso da abordagem de engenharia de
                  confiabilidade aplicada pela Pilar: não apenas recuperar o equipamento, mas entender e eliminar
                  as causas fundamentais que limitavam sua vida útil.
                </p>
              </div>

              <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter mt-8">
                <img
                  src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/0327e52e-7281-479d-8a04-d7f7413bed9d_compressed_CILINDRO-AMARRADEIRA-FINALIZADO.webp"
                  alt="Cilindro hidráulico de amarradeira industrial totalmente recuperado com melhorias de confiabilidade — resultado final após engenharia aplicada pela Pilar"
                  title="Cilindro hidráulico recuperado com engenharia de confiabilidade — resultado Pilar"
                  className="w-full h-auto object-contain p-4 bg-gp-graphite"
                  loading="lazy"
                />
                <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                  <p className="text-gray-400 text-xs text-center">
                    Cilindro hidráulico de amarradeira totalmente recuperado — resultado da engenharia de confiabilidade aplicada pela Pilar com ganho de vida útil superior a 8x
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Ganhos Operacionais */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Ganhos Operacionais</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-timer-flash-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <span className="text-gp-yellow font-semibold text-sm block mb-1">Maior Disponibilidade</span>
                    <p className="text-gray-300 text-sm leading-relaxed">Maior disponibilidade dos equipamentos com redução drástica do tempo de parada para manutenção</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-tools-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <span className="text-gp-yellow font-semibold text-sm block mb-1">Redução de Intervenções</span>
                    <p className="text-gray-300 text-sm leading-relaxed">Redução significativa das intervenções corretivas com eliminação das falhas prematuras</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-money-dollar-circle-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <span className="text-gp-yellow font-semibold text-sm block mb-1">Redução de Custos</span>
                    <p className="text-gray-300 text-sm leading-relaxed">Redução expressiva dos custos de manutenção corretiva e de peças de reposição</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <span className="text-gp-yellow font-semibold text-sm block mb-1">Maior Confiabilidade</span>
                    <p className="text-gray-300 text-sm leading-relaxed">Aumento expressivo da confiabilidade operacional dos cilindros hidráulicos</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-line-chart-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <span className="text-gp-yellow font-semibold text-sm block mb-1">Estabilidade do Processo</span>
                    <p className="text-gray-300 text-sm leading-relaxed">Maior estabilidade do processo produtivo com eliminação de paradas não programadas</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-check-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <span className="text-gp-yellow font-semibold text-sm block mb-1">Previsibilidade</span>
                    <p className="text-gray-300 text-sm leading-relaxed">Maior previsibilidade de manutenção com desgaste natural e programável dos componentes</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Diferenciais da Pilar */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Diferenciais da Pilar</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-brain-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Engenharia de Confiabilidade aplicada com metodologia de análise de modos de falha, efeitos e causas raiz</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-search-eye-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Análise de causa raiz com identificação e eliminação das origens fundamentais das falhas recorrentes</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Conhecimento profundo de aplicações industriais no setor de papel e celulose com entendimento do processo</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-lightbulb-flash-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Capacidade de desenvolver melhorias técnicas específicas para cada aplicação industrial</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-settings-3-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Recuperação completa de cilindros hidráulicos com controle dimensional e montagem de precisão</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-test-tube-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Estrutura para testes hidráulicos e validações com acompanhamento do desempenho operacional</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Galeria Técnica */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Galeria Técnica</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Foto 1 — Vedações Novas */}
                <div className="relative rounded-lg border border-gp-graphite-lighter bg-gp-graphite">
                  <div className="h-64 md:h-72 flex items-center justify-center">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/ab5c1cba-b240-4280-81d0-1f8e4197067a_compressed_VEDACOES-NOVAS-CILINDRO.webp"
                      alt="Novas vedações aplicadas durante a recuperação do cilindro hidráulico de amarradeira"
                      title="Novas vedações aplicadas durante a recuperação do cilindro de amarradeira"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 1</span>
                  </div>
                </div>
                {/* Foto 2 — Haste Nova */}
                <div className="relative rounded-lg border border-gp-graphite-lighter bg-gp-graphite">
                  <div className="h-64 md:h-72 flex items-center justify-center">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/a29376e1-6296-468f-82df-f6058dbfd713_compressed_HASTE-NOVA-CILINDRO.webp"
                      alt="Haste nova fabricada para substituição do componente desgastado do cilindro de amarradeira"
                      title="Haste nova fabricada para cilindro hidráulico de amarradeira"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 2</span>
                  </div>
                </div>
                {/* Foto 3 — Cabeçotes Cilindro Amarradeira */}
                <div className="relative rounded-lg border border-gp-graphite-lighter bg-gp-graphite">
                  <div className="h-64 md:h-72 flex items-center justify-center">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/28d99278-5322-4831-bfdd-ebda0bfc0ce6_compressed_CABECOTES-CILINDRO-AMARRADEIRA.webp"
                      alt="Cabeçotes do cilindro de amarradeira — inspeção e recuperação estrutural"
                      title="Cabeçotes do cilindro de amarradeira — inspeção e recuperação"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 3</span>
                  </div>
                </div>
                {/* Foto 4 — Cabeçotes Cilindro */}
                <div className="relative rounded-lg border border-gp-graphite-lighter bg-gp-graphite">
                  <div className="h-64 md:h-72 flex items-center justify-center">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/9ecd51ef-0b37-402f-b899-c93ba3088574_compressed_CABECOTES-CILINDRO.webp"
                      alt="Cabeçotes do cilindro — componentes estruturais recuperados e inspecionados"
                      title="Cabeçotes do cilindro — componentes estruturais recuperados"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 4</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Serviços Relacionados */}
        <section className="relative py-16 overflow-hidden border-t border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-lg font-bold text-white uppercase tracking-wider">Serviços Relacionados</h2>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/hidraulica-industrial"
                  className="inline-flex items-center gap-2 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Hidráulica Industrial
                </Link>
                <Link
                  to="/mecanica-industrial"
                  className="inline-flex items-center gap-2 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Mecânica Industrial
                </Link>
                <Link
                  to="/recuperacao-cilindros-hidraulicos"
                  className="inline-flex items-center gap-2 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Recuperação de Cilindros Hidráulicos
                </Link>
                <Link
                  to="/oficina"
                  className="inline-flex items-center gap-2 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Oficina Especializada
                </Link>
                <Link
                  to="/cases"
                  className="inline-flex items-center gap-2 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Mais Cases de Sucesso
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="relative py-20 bg-gp-graphite-light overflow-hidden">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="relative rounded-xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-gp-yellow/20 to-gp-graphite"></div>
              <div className="absolute inset-0 bg-gp-graphite/60"></div>
              <div className="relative z-10 p-8 md:p-12 text-center">
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                  Seus cilindros hidráulicos apresentam falhas prematuras e vida útil reduzida?
                </h2>
                <p className="text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed">
                  Conte com a engenharia de confiabilidade da Pilar para analisar as causas raiz, desenvolver
                  melhorias técnicas e ampliar significativamente a vida útil dos seus cilindros hidráulicos
                  industriais. Aplicamos metodologia de análise de modos de falha para transformar
                  equipamentos problemáticos em ativos confiáveis e previsíveis.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a
                    href="https://wa.me/5527993092922"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
                  >
                    <div className="w-10 h-10 bg-gp-graphite rounded-full flex items-center justify-center">
                      <i className="ri-whatsapp-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <span>Solicitar Análise de Confiabilidade</span>
                    <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
                  </a>
                  <Link
                    to="/contato"
                    className="inline-flex items-center justify-center gap-2 border border-gray-500 hover:border-gp-yellow text-gray-300 hover:text-gp-yellow px-8 py-4 rounded-full font-semibold text-sm transition-all duration-300 whitespace-nowrap"
                  >
                    <i className="ri-mail-line text-lg w-6 h-6 flex items-center justify-center"></i>
                    Falar com Engenheiro
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Back to Cases */}
        <section className="py-10 border-t border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <Link
                to="/cases"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider"
              >
                <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
                Voltar para Cases
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider"
              >
                <i className="ri-home-line w-5 h-5 flex items-center justify-center"></i>
                Voltar para Home
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />

      {/* Schema JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'Engenharia de Confiabilidade Aplicada à Recuperação de Cilindros Hidráulicos de Amarradeira | Case Grupo Pilar',
            description: 'Case de engenharia de confiabilidade aplicada à recuperação de cilindros hidráulicos de amarradeira industrial, aumentando significativamente a vida útil dos equipamentos e reduzindo falhas prematuras.',
            image: heroImage,
            author: {
              '@type': 'Organization',
              name: 'Grupo Pilar',
              url: 'https://www.pilarmanutencao.com.br',
            },
            publisher: {
              '@type': 'Organization',
              name: 'Grupo Pilar',
              logo: {
                '@type': 'ImageObject',
                url: '/images/MARCA/LOGO-GRUPO-PILAR/LOGO-GRUPO-PILAR.png',
              },
            },
            datePublished: '2025-12-05',
            dateModified: '2026-06-13',
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': 'https://www.pilarmanutencao.com.br/cases/engenharia-confiabilidade-cilindros-amarradeira',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: 'https://www.pilarmanutencao.com.br',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Cases de Sucesso',
                item: 'https://www.pilarmanutencao.com.br/cases',
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: 'Engenharia de Confiabilidade Aplicada à Recuperação de Cilindros Hidráulicos de Amarradeira',
                item: 'https://www.pilarmanutencao.com.br/cases/engenharia-confiabilidade-cilindros-amarradeira',
              },
            ],
          }),
        }}
      />
    </div>
  );
}